import { useState } from 'react';
import { Check, Edit2, X } from 'lucide-react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Podman from '@/assets/ico/vendor/podman.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import { notifySuccess } from '@/portainer/services/notifications';

import { Badge } from '@@/Badge';
import { DetailsTable } from '@@/DetailsTable';
import { Alert } from '@@/Alert';
import { Widget, WidgetBody } from '@@/Widget';
import { Input } from '@@/form-components/Input';
import { Button } from '@@/buttons';

import { usePolicy } from '../queries/usePolicy';
import { usePolicyMetadata } from '../queries/usePolicyMetadata';
import { useUpdatePolicyMutation } from '../queries/useUpdatePolicyMutation';
import {
  getEnvironmentTypeDisplayName,
  getMinimumAgentVersion,
  PolicyEnvironmentType,
} from '../types';
import { PolicyTypeBadge } from '../components/PolicyTypeBadge';
import { CreatePolicyData } from '../queries/types';

/** Props for the PolicyDetailsTab component */
interface Props {
  /** The ID of the policy to display details for */
  policyId: number;
}

const nameValidationSchema = Yup.object({
  name: Yup.string().required('Name is required').trim(),
});

export function PolicyDetailsTab({ policyId }: Props) {
  const policyQuery = usePolicy(policyId);
  const metadataQuery = usePolicyMetadata();
  const updateMutation = useUpdatePolicyMutation();

  const [isEditingName, setIsEditingName] = useState(false);

  const minimumAgentVersion = policyQuery.data?.Type
    ? getMinimumAgentVersion(
        policyQuery.data.Type,
        metadataQuery.data?.minimumAgentVersions
      )
    : null;

  async function handleSaveName(values: { name: string }) {
    if (!policyQuery.data || values.name === policyQuery.data.Name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: policyQuery.data.Id,
        payload: {
          Name: values.name,
          Type: policyQuery.data.Type,
          EnvironmentGroups: policyQuery.data.EnvironmentGroups,
          Data: policyQuery.data.Data as CreatePolicyData,
        },
      });
      notifySuccess('Success', 'Policy name updated');
      setIsEditingName(false);
    } catch {
      // Error is handled by global error handler in mutation
    }
  }

  if (policyQuery.error) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Alert color="error" title="Error loading policy">
            {(policyQuery.error as Error)?.message ?? 'Unknown error'}
          </Alert>
        </div>
      </div>
    );
  }
  if (!policyQuery.data && !policyQuery.isLoading) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Alert color="error" title="Policy not found">
            Policy with id {policyId} not found
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget>
          <WidgetBody loading={policyQuery.isLoading}>
            <DetailsTable dataCy="policy-details-table">
              {policyQuery.data?.Name && (
                <tr>
                  <td className="min-w-[150px]">Policy name</td>
                  <td data-cy="detailsTable-Policy nameValue">
                    {isEditingName ? (
                      <Formik
                        initialValues={{ name: policyQuery.data.Name }}
                        validationSchema={nameValidationSchema}
                        onSubmit={handleSaveName}
                        validateOnMount
                      >
                        {({ submitForm, isValid }) => (
                          <Form>
                            <div className="flex items-center gap-2">
                              <Field
                                as={Input}
                                name="name"
                                className="!h-8 w-64"
                                onKeyDown={(e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter' && isValid) {
                                    submitForm();
                                  }
                                  if (e.key === 'Escape') {
                                    setIsEditingName(false);
                                  }
                                }}
                                data-cy="policy-name-edit-input"
                              />
                              <Button
                                size="small"
                                color="primary"
                                onClick={submitForm}
                                disabled={updateMutation.isLoading || !isValid}
                                title="Save name"
                                icon={Check}
                                data-cy="policy-name-save-button"
                              />
                              <Button
                                size="small"
                                color="default"
                                onClick={() => setIsEditingName(false)}
                                disabled={updateMutation.isLoading}
                                title="Cancel"
                                icon={X}
                                data-cy="policy-name-cancel-button"
                              />
                            </div>
                          </Form>
                        )}
                      </Formik>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{policyQuery.data.Name}</span>
                        <Button
                          color="link"
                          size="xsmall"
                          icon={Edit2}
                          onClick={() => setIsEditingName(true)}
                          title="Edit policy name"
                          data-cy="policy-name-edit-button"
                        />
                      </div>
                    )}
                  </td>
                </tr>
              )}

              {policyQuery.data?.Type && (
                <tr>
                  <td className="min-w-[150px]">Type</td>
                  <td data-cy="detailsTable-TypeValue">
                    <PolicyTypeBadge
                      policyType={policyQuery.data.Type}
                      data-cy="detailsTable-TypeValue-badge"
                    />
                  </td>
                </tr>
              )}

              {(policyQuery.data?.EnvironmentTypes?.length ?? 0) > 0 && (
                <>
                  <tr>
                    <td
                      colSpan={2}
                      className="min-w-[150px] pt-4 pb-2 font-semibold"
                    >
                      Policy requirements
                    </td>
                  </tr>
                  <tr>
                    <td className="min-w-[150px]">
                      <span className="ml-4">Environment types</span>
                    </td>
                    <td data-cy="detailsTable-EnvironmentTypeValue">
                      <div className="flex flex-wrap gap-1">
                        {policyQuery.data?.EnvironmentTypes.map((envType) => {
                          const Icon = getEnvironmentTypeIcon(envType);
                          return (
                            <Badge
                              key={envType}
                              type="muted"
                              className="min-w-max"
                            >
                              <Icon className="h-4 w-4" />
                              {getEnvironmentTypeDisplayName(envType)}
                            </Badge>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                  {minimumAgentVersion && (
                    <tr>
                      <td className="min-w-[150px]">
                        <span className="ml-4">Agent</span>
                      </td>
                      <td data-cy="detailsTable-AgentValue">
                        &gt;= {minimumAgentVersion}
                      </td>
                    </tr>
                  )}
                  {/* TODO: Remove this hardcoded check once proper per-policy
                      Kubernetes version requirements are supported via the
                      metadata API (similar to minimumAgentVersions). */}
                  {policyQuery.data?.Type === 'registry-k8s' && (
                    <tr>
                      <td className="min-w-[150px]">
                        <span className="ml-4">Kubernetes</span>
                      </td>
                      <td data-cy="detailsTable-KubernetesValue">&gt;= 1.30</td>
                    </tr>
                  )}
                </>
              )}

              {policyQuery.data?.UpdatedAt && (
                <tr>
                  <td className="min-w-[150px]">Last updated</td>
                  <td data-cy="detailsTable-Last updatedValue">
                    <span className="text-muted">
                      {policyQuery.data.UpdatedAt
                        ? new Date(policyQuery.data.UpdatedAt).toLocaleString()
                        : 'Unknown'}
                    </span>
                  </td>
                </tr>
              )}
            </DetailsTable>
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}

/**
 * Get the appropriate icon component based on environment type.
 * @param environmentType - The policy environment type
 * @returns SVG icon component for the environment type
 */
export function getEnvironmentTypeIcon(
  environmentType?: PolicyEnvironmentType
) {
  switch (environmentType) {
    case 'kubernetes':
      return Kubernetes;
    case 'podman':
      return Podman;
    case 'docker':
    case 'swarm':
    case undefined:
    default:
      return Docker;
  }
}
